/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx, keyframes } from '@emotion/react';

import { useContext } from 'react';

import { InlineNotification, Skeleton, Typography } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import { OccupationRolesContext } from 'bundles/account-profile/components/recruiting-profile-viewer/OccupationRolesProvider';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ProfileSectionWithTitle from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileSectionWithTitle';
import ProfileViewerCard from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileViewerCard';
import OccupationSkillsAssessmentsList from 'bundles/account-profile/components/recruiting-profile-viewer/components/iMocha-skills/OccupationSkillsAssessmentsList';
import { SkillsAssessmentBreakdownPlaceholder } from 'bundles/account-profile/components/recruiting-profile-viewer/components/iMocha-skills/SkillsAssessmentBreakdown';
import RestrictHiringSolutionsGate from 'bundles/account-profile/components/recruiting-profile-viewer/utils/RestrictHiringSolutionsGate';
import { doesAnyAssessmentHaveResults } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/skillsUtils';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';

import _t from 'i18n!nls/account-profile';

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const styles = {
  bannerList: css`
    list-style-type: none;
    padding: 0;
    margin-bottom: 0;
    width: 100%;
  `,
  bannerItem: (theme: Theme) => css`
    margin-top: ${theme.spacing(16)};
    padding: 12px 16px;
    background-color: ${theme.palette.gray[100]};
  `,
  headerWrapper: (theme: Theme) => css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: ${theme.spacing(8)};
  `,
  noDesktopTopMargin: (theme: Theme) => css`
    margin-top: 0;

    ${theme.breakpoints.down('sm')} {
      margin-top: ${theme.spacing(32)};
    }
  `,
  spinningAnimation: css`
    animation: ${spin} 2s linear infinite;
  `,
};

const SkillsSection = () => {
  const { isRolesMapLoading, idToRolesMap } = useContext(OccupationRolesContext);
  const { isProfileLoading, profileState = {}, editable, isRecruiter } = useContext(ProfileContext);

  const occupationSkillsAssessments = profileState?.occupationSkillAssessments;

  if (isRolesMapLoading || isProfileLoading) {
    return (
      <ProfileSectionWithTitle title={<Skeleton />} customCss={styles.noDesktopTopMargin}>
        <ProfileViewerCard>
          <Typography variant="h2semibold">
            <Skeleton width="100%" />
          </Typography>

          <SkillsAssessmentBreakdownPlaceholder />
        </ProfileViewerCard>
      </ProfileSectionWithTitle>
    );
  }

  const doResultsExistForAnyAssessments = doesAnyAssessmentHaveResults(occupationSkillsAssessments);

  // The first check is to make sure that the occupationSkillsAssessments is not null or undefined for TS even though this is checked by the second check
  if (!occupationSkillsAssessments || !doResultsExistForAnyAssessments) {
    return null;
  }

  return (
    <ProfileSectionWithTitle title={_t('Skills')} customCss={styles.noDesktopTopMargin}>
      <OccupationSkillsAssessmentsList
        occupationSkillsAssessments={occupationSkillsAssessments}
        idToRolesMap={idToRolesMap}
        isRecruiter={isRecruiter}
        editable={editable}
      />
    </ProfileSectionWithTitle>
  );
};

const ErrorBoundSkillsSection = () => (
  <ErrorBoundaryWithTagging
    componentName="SkillsSection"
    errorComponent={
      <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
        {_t('There has been an error in the skills section of the page. Our development team will be alerted.')}
      </InlineNotification>
    }
  >
    <RestrictHiringSolutionsGate>
      <SkillsSection />
    </RestrictHiringSolutionsGate>
  </ErrorBoundaryWithTagging>
);

export default ErrorBoundSkillsSection;
